/**
 * Mana representation following Magic the Gathering color conventions
 * W = White, U = Blue, B = Black, R = Red, G = Green, C = Colorless
 */
export interface Mana {
  W: number; // White
  U: number; // Blue
  B: number; // Black
  R: number; // Red
  G: number; // Green
  C: number; // Colorless
}

/**
 * Resources generated per trigger type
 */
export interface ResourceGeneration {
  cardsDrawn: number;
  treasuresCreated: number;
  manaGenerated: Mana;
}

/**
 * Advanced configuration for resource generation
 */
export interface AdvancedConfig {
  perCast: ResourceGeneration;
  perCopy: ResourceGeneration;
  perCastOrCopy: ResourceGeneration;
}

/**
 * Main configuration for Krark calculation
 */
export interface KrarkConfiguration {
  krarksInPlay: number;
  harmonicProdigies: number;
  freeCopies: number;
  krarksThumbs: number;
  advanced: AdvancedConfig;
}

/**
 * Node types in the decision tree
 */
export enum NodeType {
  START = 'start',
  FLIP = 'flip',
  DECISION = 'decision',
  RESULT = 'result',
}

/**
 * Flip result outcomes
 */
export enum FlipResult {
  HEADS = 'heads',
  TAILS = 'tails',
  PENDING = 'pending',
}

/**
 * Advanced Krark Configuration Keys
 */
export enum AdvancedKrarkConfigurationKey {
  PER_CAST = 'perCast',
  PER_COPY = 'perCopy',
  PER_CAST_OR_COPY = 'perCastOrCopy',
}

export const AdvancedKrarkConfigurationKeys = [
  AdvancedKrarkConfigurationKey.PER_CAST,
  AdvancedKrarkConfigurationKey.PER_COPY,
  AdvancedKrarkConfigurationKey.PER_CAST_OR_COPY,
];

/**
 * State of a spell copy in the stack
 */
export interface SpellState {
  copies: number;
  spellReturned: boolean;
  totalFlips: number;
}

/**
 * Accumulated resources throughout the tree
 */
export interface AccumulatedResources {
  cardsDrawn: number;
  treasuresCreated: number;
  manaGenerated: Mana;
  copies: number;
}

/**
 * Individual node in the probability tree
 */
export interface TreeNode {
  id: string;
  type: NodeType;
  probability: number;
  flipResult?: FlipResult;
  parentId?: string;
  children: TreeNode[];
  state: SpellState;
  resources: AccumulatedResources;
  depth: number;
  isTerminal: boolean;
  // For decision nodes
  userDecision?: 'continue' | 'stop';
  // Display properties
  label: string;
  description: string;
}

/**
 * Complete tree structure for the planner
 */
export interface ProbabilityTree {
  root: TreeNode;
  totalNodes: number;
  maxDepth: number;
  terminalNodes: TreeNode[];
}

/**
 * Execution state for the interactive tree
 */
export interface ExecutionState {
  currentNodeId: string;
  history: string[];
  finalResult?: TreeNode;
  isComplete: boolean;
}

/**
 * Statistics for probability analysis
 */
export interface ProbabilityStats {
  expectedCopies: number;
  expectedCardsDrawn: number;
  expectedTreasures: number;
  expectedMana: Mana;
  probabilities: {
    allHeads: number;
    allTails: number;
    spellReturns: number;
    atLeastNCopies: Map<number, number>;
  };
}

/**
 * Coin flip simulation result
 */
export interface FlipSimulation {
  result: FlipResult;
  wasRerolled: boolean;
  rerollCount: number;
}

/**
 * Helper to create empty Mana
 */
export const emptyMana = (): Mana => ({
  W: 0,
  U: 0,
  B: 0,
  R: 0,
  G: 0,
  C: 0,
});

/**
 * Helper to create empty ResourceGeneration
 */
export const emptyResourceGeneration = (): ResourceGeneration => ({
  cardsDrawn: 0,
  treasuresCreated: 0,
  manaGenerated: emptyMana(),
});

/**
 * Helper to create default configuration
 */
export const defaultConfiguration = (): KrarkConfiguration => ({
  krarksInPlay: 1,
  harmonicProdigies: 0,
  freeCopies: 0,
  krarksThumbs: 0,
  advanced: {
    perCast: emptyResourceGeneration(),
    perCopy: emptyResourceGeneration(),
    perCastOrCopy: emptyResourceGeneration(),
  },
});

/**
 * Add two Mana objects together
 */
export const addMana = (a: Mana, b: Mana): Mana => ({
  W: a.W + b.W,
  U: a.U + b.U,
  B: a.B + b.B,
  R: a.R + b.R,
  G: a.G + b.G,
  C: a.C + b.C,
});

/**
 * Add two ResourceGeneration objects together
 */
export const addResources = (a: ResourceGeneration, b: ResourceGeneration): ResourceGeneration => ({
  cardsDrawn: a.cardsDrawn + b.cardsDrawn,
  treasuresCreated: a.treasuresCreated + b.treasuresCreated,
  manaGenerated: addMana(a.manaGenerated, b.manaGenerated),
});
